/**
 * Leviathan Web - Driver Route / Delivery Queue Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows ordered stops for a driver's daily route with drag-and-drop reorder
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  ArrowLeft,
  MapPin,
  Clock,
  Package,
  CheckCircle,
  Circle,
  Truck,
  GripVertical,
  RefreshCw,
  User,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface StopItem {
  productName: string;
  sku: string;
  quantity: number;
  unit: string;
}

interface RouteStop {
  id: string;
  stopNumber: number;
  dispatchNumber: string;
  status: string;
  routeSequence: number | null;
  scheduledTime: string | null;
  estimatedArrival: string | null;
  actualArrival: string | null;
  estimatedDuration: number | null;
  completedAt: string | null;
  customer: { id: string; name: string; customerNumber: string; phone: string | null };
  deliveryAddress: string | null;
  deliveryCity: string | null;
  deliveryState: string | null;
  orderNumber: string;
  orderId: string;
  items: StopItem[];
  totalQuantity: number;
  truck: { id: string; unitNumber: string; name: string | null } | null;
  dispatchNotes: string | null;
}

interface RouteSummary {
  totalStops: number;
  completed: number;
  remaining: number;
  totalGallons: number;
}

interface Driver {
  id: string;
  driverNumber: string;
  firstName: string;
  lastName: string;
  status: string;
}

const STATUS_STYLES: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  COMPLETED: { icon: CheckCircle, color: 'text-success-green', label: 'Completed' },
  EN_ROUTE: { icon: Truck, color: 'text-amber', label: 'En Route' },
  ARRIVED: { icon: MapPin, color: 'text-amber', label: 'At Site' },
  DELIVERING: { icon: Package, color: 'text-amber', label: 'Delivering' },
  SCHEDULED: { icon: Circle, color: 'text-text-tertiary', label: 'Pending' },
  FAILED: { icon: Circle, color: 'text-error-red', label: 'Failed' },
};

function SortableStop({ stop, isLocked }: { stop: RouteStop; isLocked: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stop.id,
    disabled: isLocked,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const statusConfig = STATUS_STYLES[stop.status] ?? STATUS_STYLES.SCHEDULED!;
  const StatusIcon = statusConfig!.icon;
  const isComplete = stop.status === 'COMPLETED';
  const isCurrent = ['EN_ROUTE', 'ARRIVED', 'DELIVERING'].includes(stop.status);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative bg-panel-dark border transition-all ${
        isCurrent
          ? 'border-amber shadow-[0_0_15px_rgba(255,149,0,0.2)]'
          : isComplete
            ? 'border-success-green/30'
            : 'border-panel-border hover:border-steel/50'
      }`}
    >
      <div className="flex items-stretch">
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          className={`flex items-center justify-center w-10 border-r border-panel-border ${
            isLocked ? 'opacity-20 cursor-default' : 'cursor-grab hover:bg-amber/10'
          }`}
        >
          <GripVertical className="w-4 h-4 text-text-tertiary" />
        </div>

        {/* Stop Number */}
        <div className={`flex items-center justify-center w-12 text-lg font-mono font-bold ${statusConfig.color}`}>
          {stop.stopNumber}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <StatusIcon className={`w-5 h-5 ${statusConfig!.color}`} />
              <span className={`text-xs font-mono uppercase tracking-wider ${statusConfig!.color}`}>
                {statusConfig!.label}
              </span>
              <Link
                to={`/dispatch`}
                className="text-steel hover:text-amber font-mono text-xs transition-colors"
              >
                {stop.dispatchNumber}
              </Link>
            </div>
            <div className="text-right">
              {stop.scheduledTime && (
                <span className="text-text-secondary font-mono text-xs">
                  <Clock className="w-3 h-3 inline mr-1" />
                  {stop.scheduledTime}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-start justify-between">
            <div>
              <Link to={`/orders/${stop.orderId}`} className="text-text-primary font-semibold hover:text-amber transition-colors">
                {stop.customer.name}
              </Link>
              <div className="text-text-tertiary text-xs font-mono mt-1">
                <MapPin className="w-3 h-3 inline mr-1" />
                {[stop.deliveryAddress, stop.deliveryCity, stop.deliveryState].filter(Boolean).join(', ') || 'No address'}
              </div>
              <div className="text-text-tertiary text-xs font-mono">
                Order: {stop.orderNumber}
              </div>
            </div>

            <div className="text-right">
              <div className="text-amber font-mono font-bold text-lg" style={{ textShadow: '0 0 8px rgba(255,149,0,0.4)' }}>
                {stop.totalQuantity.toLocaleString()} gal
              </div>
              <div className="text-text-tertiary text-xs font-mono mt-1">
                {stop.items.map((item) => item.productName).join(', ')}
              </div>
            </div>
          </div>

          {stop.dispatchNotes && (
            <div className="text-text-tertiary text-xs font-mono bg-background/50 px-2 py-1 border-l-2 border-steel/50">
              {stop.dispatchNotes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const DriverRoutePage = () => {
  const { accessToken } = useAuth();
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0] as string);
  const [stops, setStops] = useState<RouteStop[]>([]);
  const [summary, setSummary] = useState<RouteSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Fetch drivers on mount
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await fetch(`${API_URL}/api/drivers?isActive=true&limit=200`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          setDrivers(data.drivers || data.data || data || []);
        }
      } catch {
        // silent
      }
    };
    fetchDrivers();
  }, [accessToken]);

  // Fetch route when driver and date change
  useEffect(() => {
    if (selectedDriverId && selectedDate) fetchRoute();
  }, [selectedDriverId, selectedDate]);

  const fetchRoute = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ driverId: selectedDriverId, date: selectedDate || '' });
      const response = await fetch(`${API_URL}/api/dispatch/routes?${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStops(data.stops);
        setSummary(data.summary);
      }
    } catch {
      // silent
    } finally {
      setIsLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = stops.findIndex((s) => s.id === active.id);
    const newIndex = stops.findIndex((s) => s.id === over.id);

    const reordered = arrayMove(stops, oldIndex, newIndex).map((s, i) => ({
      ...s,
      stopNumber: i + 1,
      routeSequence: i + 1,
    }));

    setStops(reordered);

    // Persist to API
    setIsSaving(true);
    try {
      await fetch(`${API_URL}/api/dispatch/routes/reorder`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dispatches: reordered.map((s) => ({ id: s.id, routeSequence: s.routeSequence })),
        }),
      });
    } catch {
      // Revert on failure
      fetchRoute();
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/dispatch" className="text-text-secondary hover:text-amber transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-bold text-text-primary uppercase tracking-wider">
              Driver Routes
            </h1>
            <p className="text-text-tertiary text-sm font-mono">{formatDate(selectedDate)}</p>
          </div>
        </div>
        <button
          onClick={fetchRoute}
          disabled={isLoading || !selectedDriverId}
          className="flex items-center space-x-2 px-4 py-2 bg-panel-dark hover:bg-steel/20 text-text-secondary hover:text-steel border border-panel-border transition-all disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="text-xs font-mono uppercase">Refresh</span>
        </button>
      </div>

      {/* Controls */}
      <div className="flex items-center space-x-4">
        <div className="flex-1 max-w-xs">
          <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Driver</label>
          <select
            value={selectedDriverId}
            onChange={(e) => setSelectedDriverId(e.target.value)}
            className="w-full bg-panel-dark border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none"
          >
            <option value="">Select Driver...</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>
                {d.firstName} {d.lastName} ({d.driverNumber})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono text-text-tertiary uppercase mb-1">Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="bg-panel-dark border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none"
          />
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Total Stops', value: summary.totalStops, color: 'text-steel' },
            { label: 'Completed', value: summary.completed, color: 'text-success-green' },
            { label: 'Remaining', value: summary.remaining, color: 'text-warning-amber' },
            { label: 'Total Gallons', value: summary.totalGallons.toLocaleString(), color: 'text-amber' },
          ].map((stat) => (
            <div key={stat.label} className="relative bg-panel-dark border border-panel-border p-4">
              <div className="absolute top-0 left-0 w-2 h-2 border-l border-t border-amber" />
              <div className="absolute top-0 right-0 w-2 h-2 border-r border-t border-amber" />
              <div className="absolute bottom-0 left-0 w-2 h-2 border-l border-b border-amber" />
              <div className="absolute bottom-0 right-0 w-2 h-2 border-r border-b border-amber" />
              <span className="text-text-tertiary text-xs uppercase tracking-widest font-mono">{stat.label}</span>
              <div className={`text-2xl font-mono font-bold mt-1 ${stat.color}`} style={{ textShadow: '0 0 10px rgba(255,149,0,0.4)' }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Route Stops */}
      {!selectedDriverId ? (
        <div className="text-center py-16 text-text-tertiary font-mono">
          <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Select a driver to view their route</p>
        </div>
      ) : isLoading ? (
        <div className="text-center py-16 text-text-tertiary font-mono">
          <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" />
          <p>Loading route...</p>
        </div>
      ) : stops.length === 0 ? (
        <div className="text-center py-16 text-text-tertiary font-mono">
          <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No dispatches scheduled for this driver on this date</p>
        </div>
      ) : (
        <div className="space-y-2">
          {isSaving && (
            <div className="text-xs font-mono text-amber animate-pulse mb-2">Saving route order...</div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={stops.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {stops.map((stop) => (
                <SortableStop key={stop.id} stop={stop} isLocked={stop.status === 'COMPLETED'} />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default DriverRoutePage;
